import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import SmartImage from "@/components/common/SmartImage";
import { Loader2, Check, X, Award } from "lucide-react";

export default function ComparisonPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/comparisons/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="max-w-md mx-auto py-20 text-center" data-testid="comparison-not-found">
    <div className="font-display text-3xl">Comparison not found</div>
    <Link to="/compare" className="text-blue-600 mt-4 inline-block">← Open Compare tool</Link>
  </div>;
  if (!data) return <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin"/></div>;

  const { comparison: c, cars } = data;
  const a = cars.find((x) => x.slug === c.car_slug_a) || cars[0];
  const b = cars.find((x) => x.slug === c.car_slug_b) || cars[1];

  return (
    <article className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid={`comparison-page-${c.slug}`}>
      <SEO
        title={c.meta_title || c.title}
        description={c.meta_description || c.verdict}
        image={c.og_image || c.hero_image}
      />
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Comparison</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">{c.title}</h1>
      {c.intro && <p className="mt-4 text-muted-foreground max-w-3xl leading-relaxed">{c.intro}</p>}

      {/* Head-to-head cars */}
      {a && b && (
        <div className="mt-10 grid sm:grid-cols-2 gap-6" data-testid="comparison-cars">
          {[a, b].map((car) => (
            <Link key={car.slug} to={`/cars/${car.slug}`} className="card-premium overflow-hidden group">
              <div className="aspect-[16/9] overflow-hidden bg-[#F3F6FA]">
                <SmartImage src={car.image} alt={`${car.brand_name} ${car.model}`} label={`${car.brand_name} ${car.model}`} className="group-hover:scale-[1.04] transition-transform duration-500"/>
              </div>
              <div className="p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{car.brand_name}</div>
                <div className="font-display text-2xl mt-1">{car.model}</div>
                <div className="mt-2 text-sm text-muted-foreground">{car.body_type} · {car.fuel_types?.join(", ")}</div>
                <div className="mt-3 font-mono text-lg">{formatINR(car.price_min)} <span className="text-sm text-muted-foreground">— {formatINR(car.price_max)}</span></div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Verdict */}
      <div className="mt-10 rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-white to-[#F8FAFC] p-6" data-testid="comparison-verdict">
        <div className="text-xs uppercase tracking-[0.2em] text-blue-700 font-semibold inline-flex items-center gap-1"><Award className="w-3 h-3"/> Verdict</div>
        <p className="mt-3 text-lg leading-relaxed">{c.verdict}</p>
      </div>

      {/* Category winners */}
      {c.category_winners && Object.keys(c.category_winners).length > 0 && (
        <div className="mt-8" data-testid="comparison-winners">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Category winners</div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(c.category_winners).map(([cat, win]) => (
              <div key={cat} className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{cat}</div>
                <div className="font-display text-lg mt-1">{win}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pros & Cons per car */}
      {c.pros_cons && Object.keys(c.pros_cons).length > 0 && (
        <div className="mt-8 grid sm:grid-cols-2 gap-6" data-testid="comparison-pros-cons">
          {Object.entries(c.pros_cons).map(([carSlug, pc]) => {
            const car = cars.find((x) => x.slug === carSlug);
            return (
              <div key={carSlug} className="rounded-3xl border border-[#E5E7EB] bg-white p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{car ? `${car.brand_name} ${car.model}` : carSlug}</div>
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Pros</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {(pc.pros || []).map((p, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/>{p}</li>)}
                  </ul>
                </div>
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-widest text-rose-700 font-semibold">Cons</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {(pc.cons || []).map((n, i) => <li key={i} className="flex gap-2"><X className="w-4 h-4 text-rose-600 shrink-0 mt-0.5"/>{n}</li>)}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-12 text-center">
        <Link to={`/compare?slugs=${c.car_slug_a},${c.car_slug_b}`} data-testid="comparison-open-tool"
          className="btn-secondary h-11 px-6 inline-flex items-center gap-2 text-sm">
          Open interactive compare →
        </Link>
      </div>
    </article>
  );
}
